import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';

import WmsOsmLayer from '../../../../../../static/assets/img/wms-layers/wms_osm_layer.png';
import WmsSatelliteLayer from '../../../../../../static/assets/img/wms-layers/wms_satellite_layer.png';
import { t } from '../../../../../utils/i18-next';
import { permissionChecks } from '../../../../../utils/permission-check';
import { WmsTool } from '../../../../../viewer/tools/wms-tool';
import { WmsState, WmsToolLayerItem } from '../../../../../viewer/wms-state';
import { useOptionalUserState } from '../../../../state/user-state/user-state';
import { SkyviewContext } from '../../../skyview/skyview-state';
import { Tooltip } from '../../../tooltip/tooltip';
import { WmsLayerSelectionItem } from './wms-layer-selection-item';

interface Props {
  wmsTool: WmsTool;
}

interface WmsSourceLayer {
  src?: string;
  title: string;
  mapLayerItem: WmsToolLayerItem;
  available?: boolean;
  disabled?: boolean;
}

/**
 *  Renders a list of WMS layer selections.
 *  @param wmsTool - The WMS tool object.
 */
export const WmsLayerSelectionList = ({ wmsTool }: Props) => {
  const { skyview } = useContext(SkyviewContext);
  const userState = useOptionalUserState();
  const hasDesignToolAccess = userState?.hasDesignToolAccessForCurrentProject() ?? false;
  const [selectedLayer, setSelectedLayer] = React.useState<WmsToolLayerItem>(
    WmsState.instance.selectedLayerItem,
  );

  const wmsSourceLayerList: WmsSourceLayer[] = [
    {
      title: t('wms.tool.layers.items.none', {
        ns: 'skyview',
      }),
      mapLayerItem: WmsToolLayerItem.None,
    },
    {
      src: WmsOsmLayer,
      title: t('wms.tool.layers.items.road', { ns: 'skyview' }),
      mapLayerItem: WmsToolLayerItem.Road,
    },
    {
      src: WmsSatelliteLayer,
      title: t('wms.tool.layers.items.satellite', { ns: 'skyview' }),
      mapLayerItem: WmsToolLayerItem.Satellite,
      available: !skyview.sharedView,
      disabled: permissionChecks.hasAccessToFeature('feature:viewer:wms:layers:satellite', [
        () => hasDesignToolAccess,
      ]),
    },
  ];

  useEffect(() => {
    WmsState.instance.selectedLayerItem = selectedLayer;
  }, [wmsTool, selectedLayer]);

  const renderLayerItem = (layer: WmsSourceLayer, index: number) => {
    if (layer.available === false) {
      return null;
    }

    return (
      <li aria-current={selectedLayer === layer.mapLayerItem} key={index}>
        {layer.disabled === false ? (
          <Tooltip
            content={t('wms.tool.layers.unavailable.content', {
              ns: 'skyview',
              layer: layer.title,
            })}
            key={index}
            mainAxisOffset={22}
            placement="left"
            title={t('wms.tool.layers.unavailable.title', { ns: 'skyview' })}
            wrapChildren={true}
          >
            <WmsLayerSelectionItem
              disabled={true}
              selected={false}
              src={layer.src}
              title={layer.title}
              onLayerSelected={undefined}
            />
          </Tooltip>
        ) : (
          <WmsLayerSelectionItem
            disabled={false}
            selected={selectedLayer === layer.mapLayerItem}
            src={layer.src}
            title={layer.title}
            onLayerSelected={() => setSelectedLayer(layer.mapLayerItem)}
          />
        )}
      </li>
    );
  };

  return <StyledUl>{wmsSourceLayerList.map(renderLayerItem)}</StyledUl>;
};

const StyledUl = styled.ul`
  margin-top: 1em;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5em;
  list-style: none;
  padding: 0;
`;
